import type { SvelteComponent } from "svelte"
import CryptchaUI from "./Cryptcha.svelte"
import sha256 from "js-sha256"

if (typeof window !== "undefined") {
    console.log("Cryptcha is running in the browser")
    // @ts-ignore
    window.Cryptcha = Cryptcha
}

type SolvedCallback = (_: { solution: string; solutionId: string }) => void
type UpdateCallback = (_: {
    currentStep: number
    stepsCount: number
    solved: boolean
    correct: boolean
    error: boolean
    waitingForInit: number
}) => void

class Cryptcha {
    private siteId: string
    private baseUrl: string
    private ui: SvelteComponent
    private currentStep: number = 0
    private stepsCount: number
    private solutionId: string
    private currentTask: {
        input: string
        hashAlgorithmId: string
        hashAlgorithmCost: number
        hasher: string
        reducerAlgorithmId: string
        reducer: string
    }
    private lastSolution: string
    private minStepDelay: number = 1200
    private autoSolve: boolean = false
    private solved: boolean = false
    private correct: boolean = false
    private error: boolean = false
    private log: boolean = false
    private initWaitDelay: number = 10000
    private waitingForInit: number = 0
    private onSolved: SolvedCallback
    private onUpdate: UpdateCallback
    private destroyed: boolean = false

    private hashAlgotihms = {
        s: sha256,
    }

    constructor(options: {
        siteId: string
        baseUrl?: string
        target: HTMLElement
        autoSolve?: boolean
        minStepDelay?: number
        log?: boolean
        onSolved?: SolvedCallback
        onUpdate?: UpdateCallback
    }) {
        this.siteId = options?.siteId
        this.baseUrl = options?.baseUrl || "/api/command"
        if (options?.target) {
            this.ui = new CryptchaUI({
                target: options.target,
                props: {
                    currentStep: this.currentStep,
                    stepsCount: this.stepsCount,
                    solved: this.solved,
                    correct: this.correct,
                },
            })
        }

        if (options?.minStepDelay) {
            this.minStepDelay = options.minStepDelay
        }

        this.log = options?.log || false

        this.onSolved = options?.onSolved || (() => {})
        this.onUpdate = options?.onUpdate || (() => {})

        if (options?.autoSolve ?? true) {
            this.autoSolve = true
            this.init()
        }
    }

    private updateUI() {
        if (this.ui) {
            const newProps = {
                currentStep: this.currentStep,
                stepsCount: this.stepsCount,
                solved: this.solved,
                correct: this.correct,
                error: this.error,
                waitingForInit: this.waitingForInit,
            }

            this.ui.$set(newProps)
        }
        this.onUpdate({
            currentStep: this.currentStep,
            stepsCount: this.stepsCount,
            solved: this.solved,
            correct: this.correct,
            error: this.error,
            waitingForInit: this.waitingForInit,
        })
    }

    async init() {
        try {
            const response = await fetch(this.baseUrl + "?siteId=" + this.siteId + "&cmd=init")
            const data = await response.json()
            if (response.status !== 200) {
                throw data
            }
            if (this.log) {
                console.log("Cryptcha init", data)
            }

            this.solutionId = data.solutionId
            this.currentStep = data.currentStep
            this.stepsCount = data.stepsCount
            this.currentTask = data.task
            this.solved = false

            this.updateUI()

            if (this.autoSolve) {
                await this.solve()
            }
        } catch (e) {
            if (e?.error === "init too fast") {
                if (this.log) {
                    console.log("Cryptcha: waiting for ", this.initWaitDelay / 1000, " seconds and retrying...")
                }

                const oneSecondStep = 1000
                this.waitingForInit = this.initWaitDelay
                for (let i = 0; i < this.initWaitDelay; i += oneSecondStep) {
                    if (this.destroyed) {
                        return
                    }

                    this.waitingForInit -= oneSecondStep
                    this.updateUI()
                    await new Promise((resolve) => setTimeout(resolve, oneSecondStep))
                }
                this.waitingForInit = 0
                this.updateUI()

                this.init()
            } else {
                console.error("Cryptcha Init Error: ", e?.error || e)
                this.error = true
                this.updateUI()
            }
        }
    }

    private async next() {
        if (this.destroyed) {
            return
        }
        const response = await fetch(
            this.baseUrl + "?siteId=" + this.siteId + "&cmd=next&solutionId=" + this.solutionId
        )
        const data = await response.json()
        if (this.log) {
            console.log("Cryptcha next", data)
        }

        this.currentStep = data.currentStep
        this.currentTask = data.task
        this.updateUI()
    }

    async solve() {
        if (this.destroyed) {
            return
        }
        const start = Date.now()
        const _hasherFn = new Function("$input", "$hashFn", "$cost", "return (" + this.currentTask.hasher + ")")
        // calculate hash
        let hash = _hasherFn(
            this.currentTask.input,
            this.hashAlgotihms[this.currentTask.hashAlgorithmId],
            this.currentTask.hashAlgorithmCost
        )

        const _reducerFn = new Function("$last", "$hash", "$hashFn", "return (" + this.currentTask.reducer + ")")
        this.lastSolution = _reducerFn(this.lastSolution, hash, this.hashAlgotihms[this.currentTask.reducerAlgorithmId])
        const end = Date.now()
        const diff = end - start
        if (this.log) {
            console.log("Cryptcha task solved in", diff, "ms to", this.lastSolution)
        }
        if (diff < this.minStepDelay) {
            // delay up to minDelay
            await new Promise((resolve) => setTimeout(resolve, this.minStepDelay - diff))
        }

        if (this.currentStep + 1 < this.stepsCount) {
            await this.next()
            if (this.autoSolve) {
                await this.solve()
            }
        } else {
            await this.checkSolution()
        }
    }

    async checkSolution() {
        if (this.destroyed) {
            return
        }
        const response = await fetch(
            this.baseUrl + "?siteId=" + this.siteId + "&cmd=check&solutionId=" + this.solutionId,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ solution: this.lastSolution }),
            }
        )
        const data = await response.json()
        if (this.log) {
            console.log("Cryptcha check solution", data)
        }
        if (response.status == 200 && data.status == "ok") {
            this.solved = true
            this.correct = true
            this.updateUI()
            this.onSolved({
                solution: this.lastSolution,
                solutionId: this.solutionId,
            })
            return true
        } else {
            if (response.status == 400) {
                this.solved = true
                this.correct = false
                this.updateUI()
            }
        }
        return false
    }

    destroy() {
        this.ui.$destroy()
        this.destroyed = true
    }
}

export default Cryptcha
